import React, { useEffect, useRef } from 'react'
import { isSigningCompleteMessage } from '../../utils/signing-complete'

type SignNowEmbedFrameProps = {
  signingLink: string
  title?: string
  testId?: string
  className?: string
  /**
   * Iframe min-height. Defaults to 80vh (termsheet).
   * Pass `false` when the parent CSS owns sizing (e.g. client agreement modal).
   */
  minHeight?: string | false
  /**
   * Fired once when SignNow redirects the iframe to `/invest/signing-complete`
   * and that page posts `SIGNNOW_SIGNING_COMPLETE`. Use to close the embed;
   * keep polling as the source of truth for signed/ready state.
   */
  onSigningComplete?: () => void
}

/**
 * Inline SignNow field-invite iframe.
 * When SignNow redirects to `/invest/signing-complete`, that page postMessages
 * the parent so we can close the embed. Document signed/ready status still
 * comes from parent polling (KYC / termsheet APIs).
 */
const SignNowEmbedFrame = ({
  signingLink,
  title = 'Sign termsheet',
  testId = 'sign-now-embed-frame',
  className,
  minHeight = '80vh',
  onSigningComplete,
}: SignNowEmbedFrameProps) => {
  const completedRef = useRef(false)
  const onCompleteRef = useRef(onSigningComplete)
  onCompleteRef.current = onSigningComplete

  useEffect(() => {
    completedRef.current = false

    if (!onCompleteRef.current) return

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (!isSigningCompleteMessage(event.data)) return
      if (completedRef.current) return
      completedRef.current = true
      onCompleteRef.current?.()
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [signingLink])

  if (!signingLink.trim()) return null

  return (
    <div className={className} data-testid={testId}>
      <iframe
        src={signingLink}
        title={title}
        allow='accelerometer; camera; microphone; geolocation'
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          ...(minHeight !== false ? { minHeight } : {}),
        }}
        data-testid={`${testId}-iframe`}
      />
    </div>
  )
}

export { SignNowEmbedFrame }
